import { Risk, Asset, SoAControl, Policy, AuditResult, Incident } from '../types/iso27001';

export const initialRisks: Risk[] = [
  {
    id: 'R-001',
    description: 'Unauthorized access to sensitive customer data',
    impact: 5,
    likelihood: 3,
    owner: 'CISO',
    status: 'assessed',
    treatmentPlan: 'Implement MFA and strict access controls',
    nextReviewDate: '2023-12-01',
  },
  {
    id: 'R-002',
    description: 'Loss of availability due to DDoS attack',
    impact: 4,
    likelihood: 2,
    owner: 'IT Manager',
    status: 'identified',
    nextReviewDate: '2023-11-15',
  },
  {
    id: 'R-003',
    description: 'Insider threat (malicious employee)',
    impact: 5,
    likelihood: 1,
    owner: 'HR Director',
    status: 'mitigated',
    treatmentPlan: 'Background checks and monitoring',
    nextReviewDate: '2024-01-20',
  },
  {
    id: 'R-004',
    description: 'Vendor supply chain compromise',
    impact: 4,
    likelihood: 3,
    owner: 'Procurement',
    status: 'assessed',
    treatmentPlan: 'Vendor risk assessment program',
    nextReviewDate: '2023-12-10',
  },
  {
    id: 'R-005',
    description: 'Ransomware infection',
    impact: 5,
    likelihood: 4,
    owner: 'IT Security',
    status: 'assessed',
    treatmentPlan: 'Offline backups and EDR',
    nextReviewDate: '2023-11-30',
  }
];

export const initialAssets: Asset[] = [
  {
    id: 'A-001',
    name: 'Customer Database',
    type: 'information',
    owner: 'Data Steward',
    classification: 'confidential',
    location: 'Cloud DB',
    nextReviewDate: '2023-10-01',
  },
  {
    id: 'A-002',
    name: 'Employee Laptops',
    type: 'hardware',
    owner: 'IT Support',
    classification: 'internal',
    location: 'Office/Remote',
    nextReviewDate: '2023-09-20',
  },
  {
    id: 'A-003',
    name: 'HQ Server Room',
    type: 'hardware',
    owner: 'Facilities',
    classification: 'restricted',
    location: 'HQ Basement',
    nextReviewDate: '2024-02-15',
  },
  {
    id: 'A-004',
    name: 'CRM Source Code',
    type: 'information',
    owner: 'CTO',
    classification: 'confidential',
    location: 'Git Repository',
    nextReviewDate: '2023-12-05',
  }
];

// ISO 27001:2022 Annex A Controls (Full List)
const generateSoAControls = (): SoAControl[] => {
  const controls: SoAControl[] = [
    // Clause 4: Context of the organization
    {
      id: '4.1',
      name: 'Understanding the organization and its context',
      description: 'The organization shall determine external and internal issues that are relevant to its purpose and that affect its ability to achieve the intended outcome(s) of its information security management system.',
      applicable: false,
      justification: 'Required for ISMS scope definition.',
      implementationStatus: 'implemented',
      owner: 'CEO',
      iso27002Guidance: 'Determine external and internal issues relevant to the ISMS.',
      gapAssessmentQuestions: [
        'Have the internal and external issues that are relevant to the organization\'s ISMS determined?',
        'Have impact and the risk associated to the issues determined?',
        'Have the remediation plan for issues documented?'
      ]
    },
    {
      id: '4.2',
      name: 'Understanding the needs and expectations of interested parties',
      description: 'The organization shall determine interested parties and their requirements.',
      applicable: false,
      justification: 'Stakeholder analysis done.',
      implementationStatus: 'implemented',
      owner: 'CEO',
      iso27002Guidance: 'Identify interested parties and their requirements.',
      gapAssessmentQuestions: [
        'Has the organization determined the interested parties that are relevant to the ISMS?',
        'Has the organization determined the needs and expectations of these interested parties?',
        'Have the requirements of these interested parties been determined, including legal, regulatory and contractual requirements?'
      ]
    },
    {
      id: '4.3',
      name: 'Determining the scope of the information security management system',
      description: 'The organization shall determine the boundaries and applicability of the information security management system to establish its scope.',
      applicable: false,
      justification: 'Scope document approved.',
      implementationStatus: 'implemented',
      owner: 'CEO',
      iso27002Guidance: 'Determine the boundaries and applicability of the ISMS.',
      gapAssessmentQuestions: [
        'Have the boundaries and applicability of the ISMS been determined to establish its scope?',
        'Has the organization defined the scope of ISMS including the in scope departments, interfaces, dependences and the locations?',
        'Is ISMS scope been documented?'
      ]
    },
    // Clause 5: Leadership
    {
      id: '5.1',
      name: 'Leadership and commitment',
      description: 'Top management shall demonstrate leadership and commitment with respect to the information security management system.',
      applicable: false,
      justification: 'Management reviews held.',
      implementationStatus: 'implemented',
      owner: 'CEO',
      iso27002Guidance: 'Top management should demonstrate leadership and commitment.',
      gapAssessmentQuestions: [
        'Is the organization’s leadership commitment to the ISMS demonstrated?',
        'Has the leadership ensured the integration of the ISMS requirements into its business processes?',
        'Has the leadership ensured resources are available for the ISMS?',
        'Has the leadership communicated the importance of effective information security?'
      ]
    },
    {
      id: '5.2',
      name: 'Policy',
      description: 'Top management shall establish an information security policy.',
      applicable: false,
      justification: 'Policy published.',
      implementationStatus: 'implemented',
      owner: 'CEO',
      iso27002Guidance: 'Establish an information security policy.',
      gapAssessmentQuestions: [
        'Is there an established information security policy that is appropriate to ISMS?',
        'Does the information security policy gives a framework for setting objectives?',
        'Is the policy documented and communicated to employees and relevant interested parties?'
      ]
    },
    {
      id: '5.3',
      name: 'Organizational roles, responsibilities and authorities',
      description: 'Top management shall ensure that the responsibilities and authorities for roles relevant to information security are assigned and communicated.',
      applicable: false,
      justification: 'Org chart defined.',
      implementationStatus: 'implemented',
      owner: 'CEO',
      iso27002Guidance: 'Ensure responsibilities and authorities are assigned and communicated.',
      gapAssessmentQuestions: [
        'Are the roles, responsibilities & authorities relevant to ISMS scope clearly defined and communicated?',
        'Is the Org Chart defined and inline with the defined roles and responsibilities?',
        'Are the responsibilities and authorities for conformance and reporting on ISMS performance assigned?'
      ]
    },
    // Clause 6: Planning
    {
      id: '6.1',
      name: 'Actions to address risks and opportunities',
      description: 'When planning for the information security management system, the organization shall consider the issues referred to in 4.1 and the requirements referred to in 4.2 and determine the risks and opportunities that need to be addressed.',
      applicable: false,
      justification: 'Risk register initialized.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Determine risks and opportunities.',
      gapAssessmentQuestions: [
        'Have the internal and external issues, and the requirements of interested parties been considered to determine the risks and opportunities?',
        'Have actions to address risks and opportunities been planned, and integrated into the ISMS processes?'
      ]
    },
    {
      id: '6.1.2',
      name: 'Information security risk assessment',
      description: 'The organization shall define and apply an information security risk assessment process.',
      applicable: false,
      justification: 'Risk methodology defined.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Define and apply a risk assessment process.',
      gapAssessmentQuestions: [
        'Has an information security risk assessment process been defined?',
        'Does the process identify risks associated with loss of confidentiality, integrity and availability?',
        'Are information security risks analysed to assess the realistic likelihood and potential consequences?',
        'Are information security risks compared to the established risk criteria and prioritised?'
      ]
    },
    {
      id: '6.1.3',
      name: 'Information security risk treatment',
      description: 'The organization shall define and apply an information security risk treatment process.',
      applicable: false,
      justification: 'Treatment plan active.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Define and apply a risk treatment process.',
      gapAssessmentQuestions: [
        'Is there an information security risk treatment process to select appropriate risk treatment options?',
        'Have the controls determined been compared with ISO/IEC 27001:2022 Annex A?',
        'Has a Statement of Applicability been produced?',
        'Has the organization formulated an information security risk treatment plan?'
      ]
    },
    {
      id: '6.2',
      name: 'Information security objectives and planning to achieve them',
      description: 'The organization shall establish information security objectives at relevant functions and levels.',
      applicable: false,
      justification: 'Objectives set annually.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Establish information security objectives.',
      gapAssessmentQuestions: [
        'Have measurable ISMS objectives and targets been established, documented and communicated?',
        'In setting its objectives, has the organization determined what needs to be done, when and by whom?',
        'Is everyone within the organization’s control aware of the importance of the information security policy?'
      ]
    },
    // Clause 7: Support
    {
      id: '7.1',
      name: 'Resources',
      description: 'The organization shall determine and provide the resources needed for the establishment, implementation, maintenance and continual improvement of the information security management system.',
      applicable: false,
      justification: 'Budget approved.',
      implementationStatus: 'implemented',
      owner: 'CEO',
      iso27002Guidance: 'Determine and provide resources.',
      gapAssessmentQuestions: [
        'Has the organization determined the resources needed for ISMS?'
      ]
    },
    {
      id: '7.2',
      name: 'Competence',
      description: 'The organization shall ensure that these persons are competent on the basis of appropriate education, training, or experience.',
      applicable: false,
      justification: 'Training records maintained.',
      implementationStatus: 'implemented',
      owner: 'HR Director',
      iso27002Guidance: 'Ensure competence of persons doing work under the organization\'s control.',
      gapAssessmentQuestions: [
        'Has the organization determined the competency of the persons relevant to ISMS?',
        'Has the organization taken corrective measures to acquire the necessary competency?',
        'Has the organization retained information as evidence for showcasing that the persons have necessary competency?'
      ]
    },
    {
      id: '7.3',
      name: 'Awareness',
      description: 'Persons doing work under the organization\'s control shall be aware of the information security policy and their contribution to the effectiveness of the information security management system.',
      applicable: false,
      justification: 'Awareness program active.',
      implementationStatus: 'implemented',
      owner: 'HR Director',
      iso27002Guidance: 'Ensure awareness of information security policy.',
      gapAssessmentQuestions: [
        'Has the organization defined and documented Information Security Awareness Plan?',
        'Does the employees undergo security awareness sessions upon hire and on periodic basis?',
        'Does the organization have a method to evaluate the effectiveness of the awareness training?'
      ]
    },
    {
      id: '7.4',
      name: 'Communication',
      description: 'The organization shall determine the need for internal and external communications relevant to the information security management system.',
      applicable: false,
      justification: 'Communication plan.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Determine need for communications.',
      gapAssessmentQuestions: [
        'Has the organization developed internal and external communication plan?',
        'Does the communication plan include the details of what to share, when to share, whom to share, how to share and with whom to share?'
      ]
    },
    {
      id: '7.5.1',
      name: 'Documented information - General',
      description: 'The organization\'s information security management system shall include documented information required by this document and determined by the organization as being necessary.',
      applicable: false,
      justification: 'Doc control procedure.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Include required documented information.',
      gapAssessmentQuestions: [
        'Has the organization determined the documented information necessary for the effectiveness of the ISMS?'
      ]
    },
    {
      id: '7.5.2',
      name: 'Creating and updating',
      description: 'When creating and updating documented information the organization shall ensure appropriate identification and description, format and media, and review and approval.',
      applicable: false,
      justification: 'Templates used.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Ensure appropriate creation and updating of documented information.',
      gapAssessmentQuestions: [
        'Is the documented information in the appropriate format, and has it been identified, reviewed and approved?',
        'Has the organization defined naming conventions?',
        'Does the organization ensure the integrity of the documents by capturing version numbers and appropriate approvals?'
      ]
    },
    {
      id: '7.5.3',
      name: 'Control of documented information',
      description: 'Documented information required by the information security management system and by this document shall be controlled.',
      applicable: false,
      justification: 'DMS in use.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Control documented information.',
      gapAssessmentQuestions: [
        'Does the organization have a process to control the distribution of its documented information?',
        'Does the organization protects the documented information from loss of confidentiality, integrity and availability?',
        'Is the documented information properly stored and adequately preserved?',
        'Has the organization identified and documentation of external origin?'
      ]
    },
    // Clause 8: Operation
    {
      id: '8.1',
      name: 'Operational planning and control',
      description: 'The organization shall plan, implement and control the processes needed to meet information security requirements.',
      applicable: false,
      justification: 'SOPs in place.',
      implementationStatus: 'implemented',
      owner: 'COO',
      iso27002Guidance: 'Plan, implement and control processes.',
      gapAssessmentQuestions: [
        'Does the organization has a programme to ensure that the ISMS achieves its outcomes?',
        'Is documented evidence retained to demonstrate that processes have been carried out as planned?',
        'Are changes planned and controlled, and unintended changes reviewed?',
        'How does the organization control outsourced processes/services?'
      ]
    },
    {
      id: '8.2',
      name: 'Information security risk assessment',
      description: 'The organization shall perform information security risk assessments at planned intervals or when significant changes are proposed or occur.',
      applicable: false,
      justification: 'Annual RA.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Perform risk assessments at planned intervals.',
      gapAssessmentQuestions: [
        'Are information security risk assessments performed at planned intervals or when significant changes occur?',
        'Does the organization retain relevant documented information of the results?'
      ]
    },
    {
      id: '8.3',
      name: 'Information security risk treatment',
      description: 'The organization shall implement the information security risk treatment plan.',
      applicable: false,
      justification: 'RTP implemented.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Implement the risk treatment plan.',
      gapAssessmentQuestions: [
        'Has the information security risk treatment plan been implemented?',
        'Does the organization retain relevant documented information of the results of the information security risk treatment?'
      ]
    },
    // Clause 9: Performance evaluation
    {
      id: '9.1',
      name: 'Monitoring, measurement, analysis and evaluation',
      description: 'The organization shall evaluate the information security performance and the effectiveness of the information security management system.',
      applicable: false,
      justification: 'Metrics defined.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Evaluate performance and effectiveness.',
      gapAssessmentQuestions: [
        'Is the information security performance and effectiveness of the ISMS evaluated?',
        'How does the organization determine the processes and controls that needs to be monitored?',
        'Has the organization determined the frequency for monitoring, measurement, analysis and evaluation?',
        'Is documented information retained as evidence?'
      ]
    },
    {
      id: '9.2',
      name: 'Internal audit',
      description: 'The organization shall conduct internal audits at planned intervals.',
      applicable: false,
      justification: 'Annual audit schedule.',
      implementationStatus: 'implemented',
      owner: 'Internal Auditor',
      iso27002Guidance: 'Conduct internal audits at planned intervals.',
      gapAssessmentQuestions: [
        'Does the organization plan, establish, implement and maintain an internal audit program?',
        'Has the organization defined the frequency, methods, responsibilities and requirements for the audit program?',
        'Are internal audits conducted periodically?',
        'Are the audits performed by competent personnel?',
        'Are the results of the internal audit reported to relevant management personnel?'
      ]
    },
    {
      id: '9.3',
      name: 'Management review',
      description: 'Top management shall review the organization\'s information security management system at planned intervals.',
      applicable: false,
      justification: 'Annual review.',
      implementationStatus: 'implemented',
      owner: 'CEO',
      iso27002Guidance: 'Review the ISMS at planned intervals.',
      gapAssessmentQuestions: [
        'Does the Top Management review the effectiveness of ISMS at planned intervals?',
        'Does the review consider changes to the internal and external issues?',
        'Does the review consider feedback from interested parties?',
        'Are the results of the management review documented, acted upon and communicated?'
      ]
    },
    // Clause 10: Improvement
    {
      id: '10.1',
      name: 'Continual improvement',
      description: 'The organization shall continually improve the suitability, adequacy and effectiveness of the information security management system.',
      applicable: false,
      justification: 'Improvement log maintained.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'Continually improve the ISMS.',
      gapAssessmentQuestions: [
        'Does the organization continually improve the suitability, adequacy and effectiveness of the ISMS?'
      ]
    },
    {
      id: '10.2',
      name: 'Nonconformity and corrective action',
      description: 'When a nonconformity occurs, the organization shall react to the nonconformity and take action to control and correct it and deal with the consequences.',
      applicable: false,
      justification: 'NC procedure.',
      implementationStatus: 'implemented',
      owner: 'CISO',
      iso27002Guidance: 'React to nonconformity and take action.',
      gapAssessmentQuestions: [
        'What are the steps taken by the organization on the non conformities identified?',
        'Does the organization takes actions to control and correct the non conformities?',
        'Does the organization identifies the root cause for the non conformity?',
        'Does the Organization take steps to review the effectiveness of corrective actions taken?',
        'Is documented information retained as evidence?'
      ]
    },

    // Annex A Controls
    // 5. Organizational Controls
    { 
      id: 'A.5.1', 
      name: 'Policies for information security', 
      description: 'Information security policy and topic-specific policies shall be defined, approved by management, published, communicated to and acknowledged by relevant personnel.', 
      applicable: false, 
      justification: 'Mandatory for compliance.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Define a set of information security policies, approved by management, and published and communicated to employees and relevant external parties.',
      gapAssessmentQuestions: [
        'Do Security policies exist?',
        'Are all policies approved by management?',
        'Are policies properly communicated to employees?',
        'Are security policies subject to review?',
        'Are the reviews conducted at regular intervals?'
      ]
    },
    { 
      id: 'A.5.2', 
      name: 'Information security roles and responsibilities', 
      description: 'Information security roles and responsibilities shall be defined and allocated according to the organization needs.', 
      applicable: false, 
      justification: 'Defined in job descriptions.', 
      implementationStatus: 'implemented', 
      owner: 'HR Director',
      iso27002Guidance: 'Define and allocate information security roles and responsibilities in accordance with the organization’s information security needs.',
      gapAssessmentQuestions: [
        'Are the employees properly briefed on their information security roles and responsibilities?',
        'Are responsibilities for the protection of individual assets defined?'
      ]
    },
    { 
      id: 'A.5.3', 
      name: 'Segregation of duties', 
      description: 'Conflicting duties and areas of responsibility shall be segregated to reduce opportunities for unauthorized or unintentional modification or misuse.', 
      applicable: false, 
      justification: 'Applied in finance and IT admin.', 
      implementationStatus: 'implemented', 
      owner: 'COO',
      iso27002Guidance: 'Segregate conflicting duties and areas of responsibility to reduce opportunities for unauthorized or unintentional modification or misuse of assets.',
      gapAssessmentQuestions: [
        'Are duties and areas of responsibility separated, in order to reduce opportunities for unauthorized modification or misuse?'
      ]
    },
    { 
      id: 'A.5.4', 
      name: 'Management responsibilities', 
      description: 'Management shall require all personnel to apply information security in accordance with the established policies.', 
      applicable: false, 
      justification: 'Part of leadership commitment.', 
      implementationStatus: 'implemented', 
      owner: 'CEO',
      iso27002Guidance: 'Require all personnel to apply information security in accordance with the established policies and procedures of the organization.',
      gapAssessmentQuestions: [
        'Does the management demonstrate support of the information security policy?',
        'Does the management ensures that personnel achieve a level of awareness?',
        'Does the management ensures that personnel are provided with adequate resources?'
      ]
    },
    { 
      id: 'A.5.5', 
      name: 'Contact with authorities', 
      description: 'The organization shall establish and maintain contact with relevant authorities.', 
      applicable: false, 
      justification: 'Not required by current regulation.', 
      implementationStatus: 'not_started', 
      owner: 'Legal Counsel',
      iso27002Guidance: 'Establish and maintain contact with relevant authorities.',
      gapAssessmentQuestions: [
        'Is there a procedure documenting when, and by whom, contact with relevant authorities will be made?',
        'Is there a process, which details how and when contact, is required?',
        'Is there a process for routine contact and intelligence sharing?'
      ]
    },
    { 
      id: 'A.5.6', 
      name: 'Contact with special interest groups', 
      description: 'The organization shall establish and maintain contact with special interest groups.', 
      applicable: false, 
      justification: 'Member of local CERT.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Establish and maintain contact with special interest groups or other specialist security forums and professional associations.',
      gapAssessmentQuestions: [
        'Do relevant individuals within the organisation maintain active membership in relevant special interest groups?',
        'Does relevant individuals gain knowledge about best practices?',
        'Does relevant individuals share and exchange information about new technologies, threats or vulnerabilities?'
      ]
    },
    { 
      id: 'A.5.7', 
      name: 'Threat intelligence', 
      description: 'Information relating to information security threats shall be collected and analysed.', 
      applicable: false, 
      justification: 'Subscribed to threat feeds.', 
      implementationStatus: 'in_progress', 
      owner: 'SOC Manager',
      iso27002Guidance: 'Collect and analyse information relating to information security threats to produce threat intelligence.',
      gapAssessmentQuestions: [
        'Is there a documented policy/procedure describing process for collecting, analyzing and evaluating information related to information security threats?',
        'Does the threat intelligence program ensure that the information collected are relevant, insightful, contextual and actionable?'
      ]
    },
    { 
      id: 'A.5.8', 
      name: 'Information security in project management', 
      description: 'Information security shall be integrated into project management.', 
      applicable: false, 
      justification: 'Security gates in SDLC.', 
      implementationStatus: 'in_progress', 
      owner: 'PMO Lead',
      iso27002Guidance: 'Integrate information security into project management methods.',
      gapAssessmentQuestions: [
        'Is there a documented policy/procedure describing process to ensure information security risks related to projects are effectively addressed?',
        'Are the information security risks assessed and treated at an early stage?'
      ]
    },
    { 
      id: 'A.5.9', 
      name: 'Inventory of information and other associated assets', 
      description: 'An inventory of information and other associated assets shall be developed and maintained.', 
      applicable: false, 
      justification: 'Asset register in place.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Develop and maintain an inventory of information and other associated assets, including owners.',
      gapAssessmentQuestions: [
        'Is there an inventory of all assets associated with information and information processing facilities?',
        'Is the inventory accurate and kept up to date?',
        'Are the asset owners identified and tagged to all assets?'
      ]
    },
    { 
      id: 'A.5.10', 
      name: 'Acceptable use of information and other associated assets', 
      description: 'Rules for the acceptable use of information and of associated assets shall be identified.', 
      applicable: false, 
      justification: 'AUP signed by all staff.', 
      implementationStatus: 'implemented', 
      owner: 'HR Director',
      iso27002Guidance: 'Identify, document and implement rules for the acceptable use of information and of associated assets.',
      gapAssessmentQuestions: [
        'Is there a documented policy/procedure describing process to ensure information and other associated assets are appropriately protected, used and handled?',
        'Is the policy approved by the management and communicated to all individuals?'
      ]
    },
    { 
      id: 'A.5.11', 
      name: 'Return of assets', 
      description: 'Personnel and other interested parties as appropriate shall return all of the organization\'s assets in their possession upon change or termination of their employment, contract or agreement.', 
      applicable: false, 
      justification: 'Offboarding checklist.', 
      implementationStatus: 'implemented', 
      owner: 'HR Director',
      iso27002Guidance: 'Ensure personnel return all organizational assets in their possession upon change or termination of employment.',
      gapAssessmentQuestions: [
        'Is there a process in place to ensure all employees and external users return the organisation\'s assets on termination?',
        'Is the organization following the defined process for collecting all physical and electronic assets?'
      ]
    },
    { 
      id: 'A.5.12', 
      name: 'Classification of information', 
      description: 'Information shall be classified according to the information security needs of the organization based on confidentiality, integrity, availability and relevant interested party requirements.', 
      applicable: false, 
      justification: 'Classification policy defined.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Classify information according to the information security needs of the organization based on confidentiality, integrity, availability.',
      gapAssessmentQuestions: [
        'Is there a documented policy/procedure describing process to classify information and assets?',
        'Are the requirements for confidentiality, integrity and availability considered?',
        'Is the classification scheme defined and followed?'
      ]
    },
    { 
      id: 'A.5.13', 
      name: 'Labelling of information', 
      description: 'An appropriate set of procedures for information labelling shall be developed and implemented in accordance with the information classification scheme.', 
      applicable: false, 
      justification: 'Labels applied to documents.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Develop and implement procedures for information labelling in accordance with the information classification scheme.',
      gapAssessmentQuestions: [
        'Is there a documented policy/procedure describing process to label the information within the organization?',
        'Does the labelling process defined the contents to be included in the label?'
      ]
    },
    { 
      id: 'A.5.14', 
      name: 'Information transfer', 
      description: 'Information transfer rules, procedures and agreements shall be in place for all types of transfer facilities within the organization and between the organization and other parties.', 
      applicable: false, 
      justification: 'Secure file transfer used.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Establish rules, procedures and agreements for information transfer within the organization and with other parties.',
      gapAssessmentQuestions: [
        'Is there a documented policy/procedure describing process to maintain the security of information transferred?',
        'Are procedures for how data should be transferred made available to all employees?',
        'Are relevant technical controls in place to prevent non-authorised forms of data transfer?'
      ]
    },
    { 
      id: 'A.5.15', 
      name: 'Access control', 
      description: 'Rules to control physical and logical access to information and other associated assets shall be established.', 
      applicable: false, 
      justification: 'RBAC implemented.', 
      implementationStatus: 'implemented', 
      owner: 'IAM Lead',
      iso27002Guidance: 'Establish rules to control physical and logical access to information and other associated assets based on business and security requirements.',
      gapAssessmentQuestions: [
        'Is there a documented policy/procedure describing process to manage logical and physical access?',
        'Is the policy based on business requirements?',
        'Does the access management include the principles of "need-to-know" and "need-to-use"?'
      ]
    },
    { 
      id: 'A.5.16', 
      name: 'Identity management', 
      description: 'The full life cycle of identities shall be managed.', 
      applicable: false, 
      justification: 'IdP solution in place.', 
      implementationStatus: 'implemented', 
      owner: 'IAM Lead',
      iso27002Guidance: 'Manage the full life cycle of identities.',
      gapAssessmentQuestions: [
        'Are the employees provided with unique IDs?',
        'Shared user IDs/Accounts are only authorized when necessary?',
        'Are the Identities removed/disabled when no longer needed?'
      ]
    },
    { 
      id: 'A.5.17', 
      name: 'Authentication information', 
      description: 'Allocation and management of authentication information shall be controlled.', 
      applicable: false, 
      justification: 'Password policy enforced.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Control the allocation and management of authentication information via a management process.',
      gapAssessmentQuestions: [
        'Is there a documented policy/procedure describing process to distribute or assign authentication credentials?',
        'Is there a documented policy/procedure describing the baseline requirements of authentication credentials?',
        'Are the employees prompted to change the credentials upon first login?'
      ]
    },
    { 
      id: 'A.5.18', 
      name: 'Access rights', 
      description: 'Access rights to information and other associated assets shall be provisioned, reviewed, modified and removed in accordance with the organization\'s topic-specific policy on and rules for access control.', 
      applicable: false, 
      justification: 'Quarterly access reviews.', 
      implementationStatus: 'implemented', 
      owner: 'IAM Lead',
      iso27002Guidance: 'Provision, review, modify and remove access rights in accordance with the access control policy.',
      gapAssessmentQuestions: [
        'Are the assess rights assigned considering the business requirements and individual\'s roles and responsibilities?',
        'Is the principle of segregation of duties considered while provisioning access rights?',
        'Is there a predefined frequency for reviewing the access rights?'
      ]
    },
    { 
      id: 'A.5.19', 
      name: 'Information security in supplier relationships', 
      description: 'Processes and procedures shall be defined and implemented to manage the information security risks associated with the use of supplier\'s products or services.', 
      applicable: false, 
      justification: 'Vendor risk management.', 
      implementationStatus: 'in_progress', 
      owner: 'Procurement',
      iso27002Guidance: 'Define and implement processes to manage information security risks associated with supplier products or services.',
      gapAssessmentQuestions: [
        'Is there a documented policy/procedure describing process to manage information security risks associated with suppliers?',
        'Are the vendors/suppliers evaluated with the organization\'s requirements for information security?',
        'Are suppliers/vendors provided with documented security requirements?'
      ]
    },
    { 
      id: 'A.5.20', 
      name: 'Addressing information security within supplier agreements', 
      description: 'Relevant information security requirements shall be established and agreed with each supplier that may access, process, store, communicate, or provide infrastructure components for, the organization\'s information.', 
      applicable: false, 
      justification: 'Security clauses in contracts.', 
      implementationStatus: 'implemented', 
      owner: 'Legal Counsel',
      iso27002Guidance: 'Establish and agree on relevant information security requirements with each supplier.',
      gapAssessmentQuestions: [
        'Are the information security requirements included in contracts established with suppliers and service providers?'
      ]
    },
    { 
      id: 'A.5.21', 
      name: 'Managing information security in the ICT supply chain', 
      description: 'Processes and procedures shall be defined and implemented to manage the information security risks associated with the ICT products and services supply chain.', 
      applicable: false, 
      justification: 'Supply chain risk assessment.', 
      implementationStatus: 'not_started', 
      owner: 'Procurement',
      iso27002Guidance: 'Define and implement processes to manage information security risks associated with the ICT supply chain.'
    },
    { 
      id: 'A.5.22', 
      name: 'Monitoring, review and change management of supplier services', 
      description: 'The organization shall regularly monitor, review, evaluate and manage change in supplier information security practices and service delivery.', 
      applicable: false, 
      justification: 'Annual vendor reviews.', 
      implementationStatus: 'in_progress', 
      owner: 'Procurement',
      iso27002Guidance: 'Regularly monitor, review, evaluate and manage change in supplier information security practices and service delivery.'
    },
    { 
      id: 'A.5.23', 
      name: 'Information security for use of cloud services', 
      description: 'Processes for acquisition, use, management and exit from cloud services shall be established.', 
      applicable: false, 
      justification: 'Cloud security policy.', 
      implementationStatus: 'in_progress', 
      owner: 'Cloud Architect',
      iso27002Guidance: 'Establish processes for acquisition, use, management and exit from cloud services.'
    },
    { 
      id: 'A.5.24', 
      name: 'Information security incident management planning and preparation', 
      description: 'The organization shall plan and prepare for managing information security incidents by defining, establishing and communicating information security incident management processes, roles and responsibilities.', 
      applicable: false, 
      justification: 'Incident response plan.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Plan and prepare for managing information security incidents by defining processes, roles and responsibilities.'
    },
    { 
      id: 'A.5.25', 
      name: 'Assessment and decision on information security events', 
      description: 'The organization shall assess information security events and decide if they are to be categorized as information security incidents.', 
      applicable: false, 
      justification: 'Triage process defined.', 
      implementationStatus: 'implemented', 
      owner: 'SOC Manager',
      iso27002Guidance: 'Assess information security events and decide if they are to be categorized as information security incidents.'
    },
    { 
      id: 'A.5.26', 
      name: 'Response to information security incidents', 
      description: 'Information security incidents shall be responded to in accordance with the documented procedures.', 
      applicable: false, 
      justification: 'Playbooks available.', 
      implementationStatus: 'implemented', 
      owner: 'SOC Manager',
      iso27002Guidance: 'Respond to information security incidents in accordance with documented procedures.'
    },
    { 
      id: 'A.5.27', 
      name: 'Learning from information security incidents', 
      description: 'Knowledge gained from information security incidents shall be used to strengthen and improve the information security controls.', 
      applicable: false, 
      justification: 'Post-incident reviews.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Use knowledge gained from information security incidents to strengthen and improve information security controls.'
    },
    { 
      id: 'A.5.28', 
      name: 'Collection of evidence', 
      description: 'The organization shall establish and implement procedures for the identification, collection, acquisition and preservation of evidence related to information security events.', 
      applicable: false, 
      justification: 'Forensic procedures.', 
      implementationStatus: 'not_started', 
      owner: 'Legal Counsel',
      iso27002Guidance: 'Establish procedures for the identification, collection, acquisition and preservation of evidence.'
    },
    { 
      id: 'A.5.29', 
      name: 'Information security during disruption', 
      description: 'The organization shall plan how to maintain information security at an appropriate level during disruption.', 
      applicable: false, 
      justification: 'BCP includes security.', 
      implementationStatus: 'in_progress', 
      owner: 'BCP Manager',
      iso27002Guidance: 'Plan how to maintain information security at an appropriate level during disruption.'
    },
    { 
      id: 'A.5.30', 
      name: 'ICT readiness for business continuity', 
      description: 'ICT readiness shall be planned, implemented, maintained and tested based on business continuity objectives and ICT continuity requirements.', 
      applicable: false, 
      justification: 'DR tests conducted.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Plan, implement, maintain and test ICT readiness based on business continuity objectives.'
    },
    { 
      id: 'A.5.31', 
      name: 'Legal, statutory, regulatory and contractual requirements', 
      description: 'Legal, statutory, regulatory and contractual requirements relevant to information security and the organization\'s approach to meet these requirements shall be identified, documented and kept up to date.', 
      applicable: false, 
      justification: 'Legal register maintained.', 
      implementationStatus: 'implemented', 
      owner: 'Legal Counsel',
      iso27002Guidance: 'Identify, document and keep up to date relevant legal, statutory, regulatory and contractual requirements.'
    },
    { 
      id: 'A.5.32', 
      name: 'Intellectual property rights', 
      description: 'The organization shall ensure compliance with legal, regulatory and contractual requirements related to intellectual property rights.', 
      applicable: false, 
      justification: 'Legal review conducted.', 
      implementationStatus: 'implemented', 
      owner: 'Legal Counsel',
      iso27002Guidance: 'Ensure compliance with legal, regulatory and contractual requirements related to intellectual property rights.'
    },
    { 
      id: 'A.5.33', 
      name: 'Protection of records', 
      description: 'Records shall be protected from loss, destruction, falsification, unauthorized access and unauthorized release.', 
      applicable: false, 
      justification: 'Retention policy.', 
      implementationStatus: 'implemented', 
      owner: 'Legal Counsel',
      iso27002Guidance: 'Protect records from loss, destruction, falsification, unauthorized access and unauthorized release.'
    },
    { 
      id: 'A.5.34', 
      name: 'Privacy and protection of PII', 
      description: 'The organization shall identify and meet the requirements regarding the preservation of privacy and protection of PII according to applicable laws and regulations and contractual requirements.', 
      applicable: false, 
      justification: 'GDPR compliance.', 
      implementationStatus: 'implemented', 
      owner: 'DPO',
      iso27002Guidance: 'Identify and meet requirements regarding the preservation of privacy and protection of PII.'
    },
    { 
      id: 'A.5.35', 
      name: 'Independent review of information security', 
      description: 'The organization\'s approach to managing information security and its implementation including people, processes and technologies shall be reviewed independently at planned intervals, or when significant changes occur.', 
      applicable: false, 
      justification: 'External audit.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Review the organization’s approach to managing information security independently at planned intervals.'
    },
    { 
      id: 'A.5.36', 
      name: 'Compliance with policies, rules and standards for information security', 
      description: 'Compliance with the organization\'s information security policies, rules and standards shall be regularly reviewed.', 
      applicable: false, 
      justification: 'Compliance checks.', 
      implementationStatus: 'implemented', 
      owner: 'Compliance Manager',
      iso27002Guidance: 'Regularly review compliance with the organization’s information security policies, rules and standards.'
    },
    { 
      id: 'A.5.37', 
      name: 'Documented operating procedures', 
      description: 'Operating procedures for information processing facilities shall be documented and made available to personnel who need them.', 
      applicable: false, 
      justification: 'Runbooks created.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Document operating procedures for information processing facilities and make them available to personnel.'
    },

    // 6. People Controls
    { 
      id: 'A.6.1', 
      name: 'Screening', 
      description: 'Background verification checks on all candidates for employment shall be carried out.', 
      applicable: false, 
      justification: 'HR process.', 
      implementationStatus: 'implemented', 
      owner: 'HR Director',
      iso27002Guidance: 'Carry out background verification checks on all candidates for employment, contractors and other personnel.'
    },
    { 
      id: 'A.6.2', 
      name: 'Terms and conditions of employment', 
      description: 'The employment contractual agreements shall state the employee\'s and the organization\'s responsibilities for information security.', 
      applicable: false, 
      justification: 'In contracts.', 
      implementationStatus: 'implemented', 
      owner: 'HR Director',
      iso27002Guidance: 'State the employee’s and the organization’s responsibilities for information security in employment contractual agreements.'
    },
    { 
      id: 'A.6.3', 
      name: 'Information security awareness, education and training', 
      description: 'Personnel shall receive appropriate awareness education and training.', 
      applicable: false, 
      justification: 'Annual training.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Provide appropriate awareness education and training to personnel and relevant external parties.'
    },
    { 
      id: 'A.6.4', 
      name: 'Disciplinary process', 
      description: 'A disciplinary process shall be formalized and communicated.', 
      applicable: false, 
      justification: 'In employee handbook.', 
      implementationStatus: 'implemented', 
      owner: 'HR Director',
      iso27002Guidance: 'Formalize and communicate a disciplinary process to take action against personnel who have committed an information security breach.'
    },
    { 
      id: 'A.6.5', 
      name: 'Responsibilities after termination or change of employment', 
      description: 'Information security responsibilities and duties that remain valid after termination or change of employment shall be defined, enforced and communicated to relevant personnel and other interested parties.', 
      applicable: false, 
      justification: 'Exit interview process.', 
      implementationStatus: 'implemented', 
      owner: 'HR Director',
      iso27002Guidance: 'Define, enforce and communicate information security responsibilities that remain valid after termination or change of employment.'
    },
    { 
      id: 'A.6.6', 
      name: 'Confidentiality or non-disclosure agreements', 
      description: 'Confidentiality or non-disclosure agreements shall be defined, documented, regularly reviewed and signed.', 
      applicable: false, 
      justification: 'NDAs for all staff/vendors.', 
      implementationStatus: 'implemented', 
      owner: 'Legal Counsel',
      iso27002Guidance: 'Identify, review and sign confidentiality or non-disclosure agreements reflecting the organization’s needs.'
    },
    { 
      id: 'A.6.7', 
      name: 'Remote working', 
      description: 'Security measures shall be implemented when personnel are working remotely.', 
      applicable: false, 
      justification: 'VPN and endpoint security.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Implement security measures when personnel are working remotely to protect information accessed, processed or stored outside the organization’s premises.'
    },
    { 
      id: 'A.6.8', 
      name: 'Information security event reporting', 
      description: 'The organization shall provide a mechanism for personnel to report observed or suspected information security events through appropriate channels in a timely manner.', 
      applicable: false, 
      justification: 'Reporting button/email.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Provide a mechanism for personnel to report observed or suspected information security events through appropriate channels.'
    },

    // 7. Physical Controls
    { 
      id: 'A.7.1', 
      name: 'Physical security perimeters', 
      description: 'Security perimeters shall be defined and used to protect areas that contain sensitive information.', 
      applicable: false, 
      justification: 'Badge access to office.', 
      implementationStatus: 'implemented', 
      owner: 'Facilities Manager',
      iso27002Guidance: 'Define and use security perimeters to protect areas that contain sensitive or critical information.'
    },
    { 
      id: 'A.7.2', 
      name: 'Physical entry', 
      description: 'Secure areas shall be protected by appropriate entry controls and access points.', 
      applicable: false, 
      justification: 'Receptionist and logs.', 
      implementationStatus: 'implemented', 
      owner: 'Facilities Manager',
      iso27002Guidance: 'Protect secure areas by appropriate entry controls and access points.'
    },
    { 
      id: 'A.7.3', 
      name: 'Securing offices, rooms and facilities', 
      description: 'Physical security for offices, rooms and facilities shall be designed and implemented.', 
      applicable: false, 
      justification: 'Locked doors.', 
      implementationStatus: 'implemented', 
      owner: 'Facilities Manager',
      iso27002Guidance: 'Design and implement physical security for offices, rooms and facilities.'
    },
    { 
      id: 'A.7.4', 
      name: 'Physical security monitoring', 
      description: 'Premises shall be continuously monitored for unauthorized physical access.', 
      applicable: false, 
      justification: 'CCTV installed.', 
      implementationStatus: 'implemented', 
      owner: 'Security Guard',
      iso27002Guidance: 'Continuously monitor premises for unauthorized physical access.'
    },
    { 
      id: 'A.7.5', 
      name: 'Protecting against physical and environmental threats', 
      description: 'Protection against physical and environmental threats, such as natural disasters and other intentional or unintentional physical threats to infrastructure shall be designed and implemented.', 
      applicable: false, 
      justification: 'Fire suppression.', 
      implementationStatus: 'implemented', 
      owner: 'Facilities Manager',
      iso27002Guidance: 'Design and implement protection against physical and environmental threats.'
    },
    { 
      id: 'A.7.6', 
      name: 'Working in secure areas', 
      description: 'Security measures for working in secure areas shall be designed and implemented.', 
      applicable: false, 
      justification: 'Clean desk policy.', 
      implementationStatus: 'implemented', 
      owner: 'Facilities Manager',
      iso27002Guidance: 'Design and implement security measures for working in secure areas.'
    },
    { 
      id: 'A.7.7', 
      name: 'Clear desk and clear screen', 
      description: 'Clear desk rules for papers and removable storage media and clear screen rules for information processing facilities shall be defined and appropriately enforced.', 
      applicable: false, 
      justification: 'Policy enforced.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Define and enforce clear desk rules for papers and removable storage media and clear screen rules.'
    },
    { 
      id: 'A.7.8', 
      name: 'Equipment siting and protection', 
      description: 'Equipment shall be sited and protected to reduce the risks from environmental threats and hazards, and opportunities for unauthorized access.', 
      applicable: false, 
      justification: 'Server room secure.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Site and protect equipment to reduce risks from environmental threats and hazards and unauthorized access.'
    },
    { 
      id: 'A.7.9', 
      name: 'Security of assets off-premises', 
      description: 'Security of assets off-premises shall be ensured.', 
      applicable: false, 
      justification: 'Laptop locks.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Ensure security of assets off-premises.'
    },
    { 
      id: 'A.7.10', 
      name: 'Storage media', 
      description: 'Storage media shall be managed through their life cycle of acquisition, use, transportation and disposal.', 
      applicable: false, 
      justification: 'Media handling policy.', 
      implementationStatus: 'not_started', 
      owner: 'IT Manager',
      iso27002Guidance: 'Manage storage media through their life cycle of acquisition, use, transportation and disposal.'
    },
    { 
      id: 'A.7.11', 
      name: 'Supporting utilities', 
      description: 'Information processing facilities shall be protected from power failures and other disruptions caused by failures in supporting utilities.', 
      applicable: false, 
      justification: 'UPS installed.', 
      implementationStatus: 'implemented', 
      owner: 'Facilities Manager',
      iso27002Guidance: 'Protect information processing facilities from power failures and other disruptions caused by failures in supporting utilities.'
    },
    { 
      id: 'A.7.12', 
      name: 'Cabling security', 
      description: 'Cables carrying power, data or supporting information services shall be protected from interception, interference or damage.', 
      applicable: false, 
      justification: 'Conduits used.', 
      implementationStatus: 'implemented', 
      owner: 'Facilities Manager',
      iso27002Guidance: 'Protect cables carrying power, data or supporting information services from interception, interference or damage.'
    },
    { 
      id: 'A.7.13', 
      name: 'Equipment maintenance', 
      description: 'Equipment shall be maintained correctly to ensure its continued availability and integrity.', 
      applicable: false, 
      justification: 'Maintenance contracts.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Maintain equipment correctly to ensure its continued availability and integrity.'
    },
    { 
      id: 'A.7.14', 
      name: 'Secure disposal or re-use of equipment', 
      description: 'Items of equipment containing storage media shall be verified to ensure that any sensitive data and licensed software has been removed or securely overwritten prior to disposal or re-use.', 
      applicable: false, 
      justification: 'Wiping process defined.', 
      implementationStatus: 'implemented', 
      owner: 'IT Support',
      iso27002Guidance: 'Verify items of equipment containing storage media to ensure sensitive data and licensed software has been removed or securely overwritten prior to disposal or re-use.'
    },

    // 8. Technological Controls
    { 
      id: 'A.8.1', 
      name: 'User endpoint devices', 
      description: 'Information stored on, processed by or accessible via user endpoint devices shall be protected.', 
      applicable: false, 
      justification: 'MDM solution.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Protect information stored on, processed by or accessible via user endpoint devices.'
    },
    { 
      id: 'A.8.2', 
      name: 'Privileged access rights', 
      description: 'The allocation and use of privileged access rights shall be restricted and managed.', 
      applicable: false, 
      justification: 'PAM solution needed.', 
      implementationStatus: 'not_started', 
      owner: 'IAM Lead',
      iso27002Guidance: 'Restrict and manage the allocation and use of privileged access rights.'
    },
    { 
      id: 'A.8.3', 
      name: 'Information access restriction', 
      description: 'Access to information and other associated assets shall be restricted in accordance with the specific topic-specific policy on access control.', 
      applicable: false, 
      justification: 'ACLs on files.', 
      implementationStatus: 'implemented', 
      owner: 'Data Owner',
      iso27002Guidance: 'Restrict access to information and other associated assets in accordance with the access control policy.'
    },
    { 
      id: 'A.8.4', 
      name: 'Access to source code', 
      description: 'Read and write access to source code, development tools and software libraries shall be appropriately managed.', 
      applicable: false, 
      justification: 'GitLab permissions.', 
      implementationStatus: 'implemented', 
      owner: 'Lead Developer',
      iso27002Guidance: 'Appropriately manage read and write access to source code, development tools and software libraries.'
    },
    { 
      id: 'A.8.5', 
      name: 'Secure authentication', 
      description: 'Secure authentication technologies and procedures shall be implemented based on information access restrictions and the topic-specific policy on access control.', 
      applicable: false, 
      justification: 'MFA enforced.', 
      implementationStatus: 'implemented', 
      owner: 'IAM Lead',
      iso27002Guidance: 'Implement secure authentication technologies and procedures based on information access restrictions.'
    },
    { 
      id: 'A.8.6', 
      name: 'Capacity management', 
      description: 'The use of resources shall be monitored and tuned, and projections made of future capacity requirements to ensure the required system performance.', 
      applicable: false, 
      justification: 'Cloud monitoring.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Monitor and tune the use of resources and make projections of future capacity requirements.'
    },
    { 
      id: 'A.8.7', 
      name: 'Protection against malware', 
      description: 'Protection against malware shall be implemented and supported by appropriate user awareness.', 
      applicable: false, 
      justification: 'Antivirus installed.', 
      implementationStatus: 'implemented', 
      owner: 'Security Engineer',
      iso27002Guidance: 'Implement protection against malware and support it by appropriate user awareness.'
    },
    { 
      id: 'A.8.8', 
      name: 'Management of technical vulnerabilities', 
      description: 'Information about technical vulnerabilities of information systems being used shall be obtained and evaluated.', 
      applicable: false, 
      justification: 'Monthly scans.', 
      implementationStatus: 'implemented', 
      owner: 'Security Engineer',
      iso27002Guidance: 'Obtain and evaluate information about technical vulnerabilities of information systems and take appropriate measures.'
    },
    { 
      id: 'A.8.9', 
      name: 'Configuration management', 
      description: 'Configurations, including security configurations, of hardware, software, services and networks shall be established, documented, implemented, monitored and reviewed.', 
      applicable: false, 
      justification: 'IaC used.', 
      implementationStatus: 'implemented', 
      owner: 'DevOps Lead',
      iso27002Guidance: 'Establish, document, implement, monitor and review configurations, including security configurations.'
    },
    { 
      id: 'A.8.10', 
      name: 'Information deletion', 
      description: 'Information stored in information systems, devices or in any other storage media shall be deleted when no longer required.', 
      applicable: false, 
      justification: 'Retention policy.', 
      implementationStatus: 'implemented', 
      owner: 'Data Steward',
      iso27002Guidance: 'Delete information stored in information systems, devices or other storage media when no longer required.'
    },
    { 
      id: 'A.8.11', 
      name: 'Data masking', 
      description: 'Data masking shall be used in accordance with the organization\'s topic-specific policy on access control and other related topic-specific policies, and business requirements.', 
      applicable: false, 
      justification: 'PII masked in logs.', 
      implementationStatus: 'implemented', 
      owner: 'Lead Developer',
      iso27002Guidance: 'Use data masking in accordance with the access control policy and business requirements.'
    },
    { 
      id: 'A.8.12', 
      name: 'Data leakage prevention', 
      description: 'Data leakage prevention measures shall be applied to systems, networks and any other devices that process, store or transmit sensitive information.', 
      applicable: false, 
      justification: 'DLP agent installed.', 
      implementationStatus: 'in_progress', 
      owner: 'Security Engineer',
      iso27002Guidance: 'Apply data leakage prevention measures to systems, networks and any other devices that process, store or transmit sensitive information.'
    },
    { 
      id: 'A.8.13', 
      name: 'Information backup', 
      description: 'Backup copies of information, software and systems shall be maintained and regularly tested in accordance with the agreed topic-specific policy on backup.', 
      applicable: false, 
      justification: 'Daily backups.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Maintain and regularly test backup copies of information, software and systems.'
    },
    { 
      id: 'A.8.14', 
      name: 'Redundancy of information processing facilities', 
      description: 'Information processing facilities shall be implemented with redundancy sufficient to meet availability requirements.', 
      applicable: false, 
      justification: 'HA clusters.', 
      implementationStatus: 'implemented', 
      owner: 'IT Architect',
      iso27002Guidance: 'Implement information processing facilities with redundancy sufficient to meet availability requirements.'
    },
    { 
      id: 'A.8.15', 
      name: 'Logging', 
      description: 'Logs that record activities, exceptions, faults and other relevant events shall be produced, stored, protected and analysed.', 
      applicable: false, 
      justification: 'SIEM implemented.', 
      implementationStatus: 'implemented', 
      owner: 'SOC Analyst',
      iso27002Guidance: 'Produce, store, protect and analyse logs that record activities, exceptions, faults and other relevant events.'
    },
    { 
      id: 'A.8.16', 
      name: 'Monitoring activities', 
      description: 'Networks, systems and applications shall be monitored for anomalous behaviour and appropriate actions taken to evaluate potential information security incidents.', 
      applicable: false, 
      justification: '24/7 monitoring.', 
      implementationStatus: 'implemented', 
      owner: 'SOC Manager',
      iso27002Guidance: 'Monitor networks, systems and applications for anomalous behaviour and take appropriate actions.'
    },
    { 
      id: 'A.8.17', 
      name: 'Clock synchronization', 
      description: 'The clocks of all relevant information processing systems within the organization or security domain shall be synchronized to a single reference time source.', 
      applicable: false, 
      justification: 'NTP configured.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Synchronize the clocks of all relevant information processing systems to a single reference time source.'
    },
    { 
      id: 'A.8.18', 
      name: 'Use of privileged utility programs', 
      description: 'The use of utility programs that can be capable of overriding system and application controls shall be restricted and tightly controlled.', 
      applicable: false, 
      justification: 'Restricted access.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Restrict and tightly control the use of utility programs capable of overriding system and application controls.'
    },
    { 
      id: 'A.8.19', 
      name: 'Installation of software on operational systems', 
      description: 'Procedures and measures shall be implemented to securely manage software installation on operational systems.', 
      applicable: false, 
      justification: 'App whitelisting.', 
      implementationStatus: 'implemented', 
      owner: 'IT Manager',
      iso27002Guidance: 'Implement procedures and measures to securely manage software installation on operational systems.'
    },
    { 
      id: 'A.8.20', 
      name: 'Networks security', 
      description: 'Networks shall be secured and managed to protect the information in systems and applications.', 
      applicable: false, 
      justification: 'Firewalls managed.', 
      implementationStatus: 'implemented', 
      owner: 'Network Engineer',
      iso27002Guidance: 'Secure and manage networks to protect the information in systems and applications.'
    },
    { 
      id: 'A.8.21', 
      name: 'Security of network services', 
      description: 'Security mechanisms, service levels and management requirements of all network services shall be identified, implemented and monitored.', 
      applicable: false, 
      justification: 'SLA monitoring.', 
      implementationStatus: 'implemented', 
      owner: 'Network Engineer',
      iso27002Guidance: 'Identify, implement and monitor security mechanisms, service levels and management requirements of all network services.'
    },
    { 
      id: 'A.8.22', 
      name: 'Segregation of networks', 
      description: 'Groups of information services, users and information systems shall be segregated on networks.', 
      applicable: false, 
      justification: 'VLANs used.', 
      implementationStatus: 'implemented', 
      owner: 'Network Engineer',
      iso27002Guidance: 'Segregate groups of information services, users and information systems on networks.'
    },
    { 
      id: 'A.8.23', 
      name: 'Web filtering', 
      description: 'Access to external websites shall be managed to reduce exposure to malicious content.', 
      applicable: false, 
      justification: 'Proxy filter.', 
      implementationStatus: 'implemented', 
      owner: 'Security Engineer',
      iso27002Guidance: 'Manage access to external websites to reduce exposure to malicious content.'
    },
    { 
      id: 'A.8.24', 
      name: 'Use of cryptography', 
      description: 'Rules for the effective use of cryptography, including cryptographic key management, shall be defined and implemented.', 
      applicable: false, 
      justification: 'Encryption policy.', 
      implementationStatus: 'implemented', 
      owner: 'CISO',
      iso27002Guidance: 'Define and implement rules for the effective use of cryptography, including cryptographic key management.'
    },
    { 
      id: 'A.8.25', 
      name: 'Secure development life cycle', 
      description: 'Rules for the secure development of software and systems shall be established and applied.', 
      applicable: false, 
      justification: 'DevSecOps pipeline.', 
      implementationStatus: 'in_progress', 
      owner: 'AppSec Lead',
      iso27002Guidance: 'Establish and apply rules for the secure development of software and systems.'
    },
    { 
      id: 'A.8.26', 
      name: 'Application security requirements', 
      description: 'Information security requirements shall be identified, specified and approved when developing or acquiring applications.', 
      applicable: false, 
      justification: 'Requirements gathering.', 
      implementationStatus: 'implemented', 
      owner: 'Lead Developer',
      iso27002Guidance: 'Identify, specify and approve information security requirements when developing or acquiring applications.'
    },
    { 
      id: 'A.8.27', 
      name: 'Secure system architecture and engineering principles', 
      description: 'Principles for engineering secure systems shall be established, documented, maintained and applied to any information system development activities.', 
      applicable: false, 
      justification: 'Architecture review.', 
      implementationStatus: 'implemented', 
      owner: 'IT Architect',
      iso27002Guidance: 'Establish, document, maintain and apply principles for engineering secure systems.'
    },
    { 
      id: 'A.8.28', 
      name: 'Secure coding', 
      description: 'Secure coding principles shall be applied to software development.', 
      applicable: false, 
      justification: 'OWASP training.', 
      implementationStatus: 'in_progress', 
      owner: 'Lead Developer',
      iso27002Guidance: 'Apply secure coding principles to software development.'
    },
    { 
      id: 'A.8.29', 
      name: 'Security testing in development and acceptance', 
      description: 'Security testing processes shall be defined and implemented in the development life cycle.', 
      applicable: false, 
      justification: 'SAST/DAST.', 
      implementationStatus: 'in_progress', 
      owner: 'AppSec Lead',
      iso27002Guidance: 'Define and implement security testing processes in the development life cycle.'
    },
    { 
      id: 'A.8.30', 
      name: 'Outsourced development', 
      description: 'The organization shall direct, monitor and review the activities related to outsourced system development.', 
      applicable: false, 
      justification: 'Contractual terms.', 
      implementationStatus: 'implemented', 
      owner: 'Lead Developer',
      iso27002Guidance: 'Direct, monitor and review activities related to outsourced system development.'
    },
    { 
      id: 'A.8.31', 
      name: 'Separation of development, test and production environments', 
      description: 'Development, testing and production environments shall be separated to reduce the risks of unauthorized access or changes to the operational environment.', 
      applicable: false, 
      justification: 'Separate VPCs.', 
      implementationStatus: 'implemented', 
      owner: 'DevOps Lead',
      iso27002Guidance: 'Separate development, testing and production environments.'
    },
    { 
      id: 'A.8.32', 
      name: 'Change management', 
      description: 'Changes to information processing facilities and information systems shall be subject to change management procedures.', 
      applicable: false, 
      justification: 'CAB meetings.', 
      implementationStatus: 'implemented', 
      owner: 'Change Manager',
      iso27002Guidance: 'Subject changes to information processing facilities and information systems to change management procedures.'
    },
    { 
      id: 'A.8.33', 
      name: 'Test information', 
      description: 'Test information shall be appropriately selected, protected and managed.', 
      applicable: false, 
      justification: 'Synthetic data used.', 
      implementationStatus: 'implemented', 
      owner: 'QA Lead',
      iso27002Guidance: 'Appropriately select, protect and manage test information.'
    },
    { 
      id: 'A.8.34', 
      name: 'Protection of information systems during audit testing', 
      description: 'Audit tests and other assurance activities involving operational systems shall be planned and agreed between the tester and appropriate management.', 
      applicable: false, 
      justification: 'Audit plan approved.', 
      implementationStatus: 'implemented', 
      owner: 'Internal Auditor',
      iso27002Guidance: 'Plan and agree on audit tests and other assurance activities involving operational systems.'
    },
  ];
  
  return controls;
};

export const initialSoA: SoAControl[] = generateSoAControls();

export const initialPolicies: Policy[] = [
  {
    id: 'P-001',
    title: 'Access Control Policy',
    sections: [
      { id: 's1', title: '1. Purpose', content: 'The purpose of this policy is to ensure that access to information and information processing facilities is controlled.' },
      { id: 's2', title: '2. Scope', content: 'This policy applies to all employees, contractors, and third parties.' },
      { id: 's3', title: '3. Policy Statement', content: 'Access to systems shall be granted based on the principle of least privilege.' }
    ],
    version: '1.0',
    createdDate: '2023-01-15',
    lastUpdated: new Date().toISOString(),
    nextReviewDate: '2024-01-01',
    status: 'draft',
    generatedByAI: false,
    owner: 'CISO',
    approver: 'CEO',
    history: [],
    coveredControls: ['A.5.1', 'A.5.15', 'A.8.3'],
    frameworkCode: 'ISO27001'
  },
  {
    id: 'P-002',
    title: 'Information Security Policy',
    sections: [
      { id: 's1', title: '1. Overview', content: 'High level commitment to security.' },
    ],
    version: '1.1',
    createdDate: '2022-11-01',
    lastUpdated: new Date().toISOString(),
    nextReviewDate: '2023-11-01', // Past date to trigger notification
    status: 'approved',
    generatedByAI: false,
    owner: 'IT Director',
    approver: 'Board',
    history: [
      {
        version: '1.0',
        date: '2022-01-01',
        sections: [{ id: 's1', title: '1. Overview', content: 'Old content.' }],
        changedBy: 'Admin'
      }
    ],
    coveredControls: ['A.5.1'],
    frameworkCode: 'ISO27001'
  }
];

export const initialAuditResults: AuditResult[] = [];

export const initialIncidents: Incident[] = [
  {
    id: 'INC-001',
    title: 'Phishing Email Reported',
    description: 'Employee received a suspicious email asking for credentials.',
    dateOccurred: '2023-10-25',
    severity: 'medium',
    status: 'resolved',
    reportedBy: 'John Doe',
    rootCause: 'Lack of awareness training',
    correctiveActions: 'Conducted phishing simulation and training.'
  },
  {
    id: 'INC-002',
    title: 'Laptop Theft',
    description: 'Company laptop stolen from employee car.',
    dateOccurred: '2023-11-10',
    severity: 'high',
    status: 'open',
    reportedBy: 'Jane Smith',
    rootCause: 'Negligence',
    correctiveActions: 'Remote wipe initiated. Police report filed.'
  },
  {
    id: 'INC-003',
    title: 'Failed Login Attempts',
    description: 'Multiple failed login attempts detected on Admin account.',
    dateOccurred: '2023-11-12',
    severity: 'low',
    status: 'investigating',
    reportedBy: 'System Monitor',
    rootCause: 'Brute force attack',
    correctiveActions: 'IP blocked.'
  }
];
